import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { Breadcrumb, Col, Divider, Row, Space, Spin } from 'antd'
import { isEmpty } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import { HistoryLink } from '../../../common/components/sections'
import { StyledPageSubTitle } from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  EVENT_TYPES,
  METADATA_TYPES,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useCuisineTypesQuery,
  useDishTypesQuery,
  useDocumentTitle,
  useUpdatedIngredientSubscription,
} from '../../../common/hooks'

import { renderChallengeEvent } from '../../helpers'
import { useChallengeQuery } from '../../hooks'
import {
  ChallengeImageDetailsForm,
  ChallengeRelationsDetailsForm,
  ChallengeSimpleDetailsForm,
} from '../forms'

const ChallengeDetailsPage = () => {
  const { id } = useParams()

  const isChallengePageDirty = useSelector(
    store =>
      store.challenges.challenge.isImageDetailsLoading ||
      store.challenges.challenge.isRelationsDirty ||
      store.challenges.challenge.isSimpleDetailsDirty,
  )

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    data,
    refetch,
  } = useChallengeQuery(id)

  useUpdatedIngredientSubscription(
    id,
    isChallengePageDirty,
    METADATA_TYPES.CHALLENGES.value,
    refetch,
  )

  const {
    data: cuisineTypes,
    options: cuisineTypeOptions,
    isLoading: isLoadingCuisineTypes,
    error: cuisineTypesError,
  } = useCuisineTypesQuery()

  const {
    data: dishTypes,
    options: dishTypesOptions,
    isLoading: isLoadingDishTypes,
    error: dishTypesError,
  } = useDishTypesQuery()

  useDocumentTitle(`${data?.name || 'Loading Challenge...'}`)

  useEffect(() => {
    if (fetchError || cuisineTypesError || dishTypesError) {
      showErrorNotification({
        title: 'Challenge error',
        description: fetchError || cuisineTypesError || dishTypesError,
      })
    }
  }, [dishTypesError, fetchError, cuisineTypesError])

  return isFetchLoading ||
    isLoadingCuisineTypes ||
    isLoadingDishTypes ||
    isEmpty(data) ? (
    <Spin />
  ) : (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link
                  to={APP_ROUTES.MOBILE_APP_ONLY.subMenuItems.CHALLENGES.link}
                >
                  {APP_ROUTES.MOBILE_APP_ONLY.subMenuItems.CHALLENGES.name}
                </Link>
              </Breadcrumb.Item>

              <Breadcrumb.Item>
                <Link
                  to={`${APP_ROUTES.MOBILE_APP_ONLY.subMenuItems.CHALLENGES.link}/${data.id}`}
                >
                  {data.name}
                </Link>
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>

          <Space>
            <UpdateDataText
              updatedAt={data.updatedAt}
              updatedBy={data.updatedBy?.name}
            />

            <HistoryLink
              renderEvent={renderChallengeEvent}
              entityId={id}
              eventTypes={[EVENT_TYPES.UPDATE]}
              eventMetadataTypes={[EVENT_METADATA_TYPES.CHALLENGE]}
            />
          </Space>
        </Row>
      </StyledPageSubTitle>
      <ChallengeSimpleDetailsForm
        challenge={data}
        maxPosition={data.maxPosition}
      />
      <Divider />
      <ChallengeImageDetailsForm challenge={data} />
      <Divider />
      {!isEmpty(cuisineTypes) && !isEmpty(dishTypes) && (
        <ChallengeRelationsDetailsForm
          challenge={data}
          dishTypeOptions={dishTypesOptions}
          cuisineTypeOptions={cuisineTypeOptions}
        />
      )}
    </>
  )
}

export default ChallengeDetailsPage
